import { Sparkline } from '@/components/charts/Sparkline';
import {
  Badge,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  Tooltip,
} from '@/components/ui';
import { EntityLink } from '@/components/layout/workspace/EntityLink';
import { at } from '../../model';
import { usePrefs, useViewLoading } from '../../prefs';
import { Body, Page } from '../common';
import { NODES } from './data';

/*
  Узлы. Строка помнит, что с ней было: зарубка слева — инциденты за сутки, мини-след процессора
  рядом с числом (R25). Имя узла живое и открывает его вкладкой (R26, R37). Пока список едет —
  сетка той же формы (R31).
*/

const SKELETON = [
  { width: 'w-24' },
  { width: 'w-28' },
  { width: 'w-16' },
  { width: 'w-24', align: 'right' as const },
  { width: 'w-12', align: 'right' as const },
];

export function InfraNodes() {
  const { dense } = usePrefs();
  const loading = useViewLoading();

  return (
    <Page>
      <PageHeader
        title="Узлы"
        meta={
          <>
            <span>71 узел</span>
            <span>зарубка слева — инциденты за сутки</span>
          </>
        }
      />
      <Body>
        <div className="overflow-hidden rounded-lg border border-line bg-card px-4">
          {loading ? (
            <TableSkeleton columns={SKELETON} rows={6} dense={dense} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Узел</TableHead>
                  <TableHead>Площадка</TableHead>
                  <TableHead>Состояние</TableHead>
                  <TableHead className="text-right">Процессор</TableHead>
                  <TableHead className="text-right">Память</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {NODES.map((n) => (
                  <TableRow key={n.id} className={dense ? 'h-9' : undefined}>
                    <TableCell className="relative">
                      {n.incidents > 0 && (
                        <Tooltip content={`${n.incidents} за сутки`} side="right">
                          <span
                            className="absolute top-1.5 bottom-1.5 left-0 w-1 rounded-full bg-sev-4-line"
                            aria-label={`Инцидентов за сутки: ${n.incidents}`}
                          />
                        </Tooltip>
                      )}
                      <span className="pl-2">
                        <EntityLink
                          to={at(`/apps/infra/node/${n.id}`)}
                          kind="host"
                          name={n.id}
                          subtitle={`Гипервизор · ${n.role}`}
                          state={{ label: n.state, variant: n.state === 'Проблема' ? 'urgent' : 'success' }}
                          metrics={[
                            { label: 'Процессор', value: `${n.cpu} %`, trend: n.cpuTrend },
                            { label: 'Память', value: `${n.mem} %` },
                          ]}
                          note={n.incidents ? `${n.incidents} за сутки` : 'Спокойно'}
                        />
                      </span>
                    </TableCell>
                    <TableCell className="text-fg-muted">{n.role}</TableCell>
                    <TableCell>
                      <Badge variant={n.state === 'Проблема' ? 'urgent' : 'success'} solid>
                        {n.state}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="inline-flex items-center gap-2">
                        <Sparkline
                          values={n.cpuTrend}
                          width={56}
                          height={16}
                          endDot={false}
                          tone={n.cpu > 60 ? 'danger' : 'muted'}
                        />
                        <span className="tabular-nums">{n.cpu} %</span>
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{n.mem} %</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Body>
    </Page>
  );
}
